import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Play, Trash2, Bell, BellOff, Loader2, CalendarClock, Bookmark, CheckCircle2, FileText, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SavedScenariosList({ savedScenarios, assetClasses, currency, onRefresh, onRun }) {
  const acMap = Object.fromEntries(assetClasses.map(a => [a.asset_class_id, a.name]));
  const [runningId, setRunningId] = useState(null);
  const [executingId, setExecutingId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState(new Set());

  const toggleReviewsExpanded = (id) => {
    setExpandedReviews((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleWatch = async (scn) => {
    try {
      const res = await fetch(`${API}/scenarios/${scn.scenario_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ watch_enabled: !scn.watch_enabled })
      });
      if (res.ok) {
        toast.success(scn.watch_enabled ? 'Watch disabled' : 'Watch enabled');
        onRefresh();
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      console.error('toggleWatch failed:', err);
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/scenarios/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Saved scenario deleted');
        onRefresh();
      } else {
        toast.error('Failed to delete');
      }
    } catch (err) {
      console.error('delete failed:', err);
      toast.error('Failed to delete');
    }
  };

  const handleRun = async (scn) => {
    setRunningId(scn.scenario_id);
    try {
      const res = await fetch(`${API}/scenarios/${scn.scenario_id}/run`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        onRun(data);
        toast.success(`Loaded "${scn.name}"`);
      } else {
        toast.error('Failed to run scenario');
      }
    } catch (err) {
      console.error('run failed:', err);
      toast.error('Failed to run scenario');
    } finally {
      setRunningId(null);
    }
  };

  const handleExecute = async (scn) => {
    setExecutingId(scn.scenario_id);
    try {
      const res = await fetch(`${API}/scenarios/${scn.scenario_id}/execute`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(`"${scn.name}" marked as executed`);
        onRefresh();
      } else {
        let detail = null;
        try { detail = JSON.parse(await res.text()).detail; } catch { /* ignore */ }
        toast.error(typeof detail === 'string' ? detail : 'Failed to mark executed');
      }
    } catch (err) {
      console.error('execute failed:', err);
      toast.error('Failed to mark executed');
    } finally {
      setExecutingId(null);
    }
  };

  const handleReview = async (scn) => {
    setReviewingId(scn.scenario_id);
    try {
      const res = await fetch(`${API}/scenarios/${scn.scenario_id}/review`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Decision Review generated');
        // Auto-expand reviews on new review
        setExpandedReviews((prev) => {
          const next = new Set(prev);
          next.add(scn.scenario_id);
          return next;
        });
        onRefresh();
      } else {
        let detail = null;
        try { detail = JSON.parse(await res.text()).detail; } catch { /* ignore */ }
        toast.error(typeof detail === 'string' ? detail : 'Failed to generate review');
      }
    } catch (err) {
      console.error('review failed:', err);
      toast.error('Failed to generate review');
    } finally {
      setReviewingId(null);
    }
  };

  if (savedScenarios.length === 0) {
    return (
      <div className="py-12 text-center">
        <Bookmark className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" strokeWidth={1.2} />
        <p className="text-sm font-medium">No saved scenarios yet</p>
        <p className="text-xs text-muted-foreground mt-1">
          Build a scenario and save it to revisit later
        </p>
      </div>
    );
  }

  return (
    <div data-testid="saved-scenarios-list" className="space-y-3">
      {savedScenarios.map((scn) => (
        <Card
          key={scn.scenario_id}
          data-testid={`saved-scenario-${scn.scenario_id}`}
          className="border border-border"
        >
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-sm truncate">{scn.name}</p>
                  <Badge variant="outline" className="text-xs">{scn.currency}</Badge>
                  {scn.executed && (
                    <Badge className="bg-success/10 text-success border-success/20 text-xs">
                      <CheckCircle2 className="h-3 w-3 mr-1" strokeWidth={1.8} /> Executed
                    </Badge>
                  )}
                  {!scn.executed && scn.watch_enabled && (
                    <Badge className="bg-accent/10 text-accent border-accent/20 text-xs">
                      <Bell className="h-3 w-3 mr-1" strokeWidth={1.8} /> Watching
                    </Badge>
                  )}
                </div>
                {scn.notes && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{scn.notes}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" strokeWidth={1.8} />
                  Saved {format(new Date(scn.created_at), 'MMM dd, yyyy')}
                  {scn.last_triggered_at && (
                    <span className="ml-2">· Last fired {format(new Date(scn.last_triggered_at), 'MMM dd')}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  data-testid={`run-scenario-${scn.scenario_id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRun(scn)}
                  disabled={runningId === scn.scenario_id}
                  className="h-8 px-2"
                  title="Run with current holdings"
                >
                  {runningId === scn.scenario_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" strokeWidth={1.8} />
                  )}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      data-testid={`delete-scenario-${scn.scenario_id}`}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete saved scenario?</AlertDialogTitle>
                      <AlertDialogDescription>
                        "{scn.name}" will be permanently removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(scn.scenario_id)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            {/* Compact scenario summary */}
            <div className="space-y-1">
              {scn.scenarios.map((step, idx) => (
                <div key={`${scn.scenario_id}-step-${idx}`} className="flex items-center gap-2 text-xs">
                  <Badge
                    className={
                      step.type === 'Buy'
                        ? 'bg-success/10 text-success border-success/20 text-[10px] h-5'
                        : 'bg-destructive/10 text-destructive border-destructive/20 text-[10px] h-5'
                    }
                  >
                    {step.type}
                  </Badge>
                  <span className="font-medium">{step.symbol}</span>
                  <span className="text-muted-foreground">{acMap[step.asset_class_id] || step.asset_class_id}</span>
                  <span className="tabular-nums ml-auto">
                    {step.quantity} × {scn.currency} {step.price.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            {/* Decision Outcome Tracker */}
            {scn.executed ? (
              <div className="pt-2 border-t border-border space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success" strokeWidth={1.8} />
                    <span className="text-xs text-muted-foreground">
                      Executed {scn.executed_at && format(new Date(scn.executed_at), 'MMM dd, yyyy')}
                    </span>
                  </div>
                  <Button
                    data-testid={`review-scenario-${scn.scenario_id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleReview(scn)}
                    disabled={reviewingId === scn.scenario_id}
                    className="h-7 text-xs"
                  >
                    {reviewingId === scn.scenario_id ? (
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="mr-1 h-3 w-3" strokeWidth={1.8} />
                    )}
                    Run Review
                  </Button>
                </div>

                {scn.reviews && scn.reviews.length > 0 && (
                  <div className="space-y-2">
                    <button
                      data-testid={`toggle-reviews-${scn.scenario_id}`}
                      onClick={() => toggleReviewsExpanded(scn.scenario_id)}
                      className="flex items-center gap-1 text-xs font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                      <FileText className="h-3 w-3" strokeWidth={1.8} />
                      {scn.reviews.length} review{scn.reviews.length !== 1 ? 's' : ''}
                      {expandedReviews.has(scn.scenario_id)
                        ? <ChevronUp className="h-3 w-3" />
                        : <ChevronDown className="h-3 w-3" />
                      }
                    </button>
                    {expandedReviews.has(scn.scenario_id) && (
                      <div className="space-y-2">
                        {scn.reviews.slice().reverse().map((rev) => (
                          <div
                            key={rev.review_id}
                            data-testid={`review-${rev.review_id}`}
                            className="p-3 bg-accent/5 border border-accent/20 rounded-md space-y-2"
                          >
                            <div className="flex items-center justify-between text-xs">
                              <Badge variant="outline" className="text-[10px] h-5">
                                {rev.label === 'user' ? 'On-demand' : rev.label}
                              </Badge>
                              <span className="text-muted-foreground">
                                {format(new Date(rev.generated_at), 'MMM dd, yyyy')} · {rev.days_since_execution}d after
                              </span>
                            </div>
                            <div className="space-y-1">
                              {rev.rows.map((r, i) => (
                                <div key={`${rev.generated_at}-${r.symbol}-${i}`} className="flex justify-between items-center text-xs">
                                  <span className="text-muted-foreground">
                                    {r.type} {r.symbol}: {scn.currency} {r.executed_price.toFixed(2)} → {scn.currency} {r.current_price.toFixed(2)}
                                  </span>
                                  <span className={`tabular-nums font-semibold ${
                                    r.delta_per_share === 0 ? 'text-muted-foreground' :
                                    (r.type === 'Sell' ? (r.delta_per_share < 0 ? 'text-success' : 'text-destructive')
                                                       : (r.delta_per_share > 0 ? 'text-success' : 'text-destructive'))
                                  }`}>
                                    {r.delta_per_share > 0 ? '+' : ''}{r.delta_per_share.toFixed(2)}/sh
                                  </span>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs leading-relaxed whitespace-pre-wrap pt-1 border-t border-accent/20">
                              {rev.analysis}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between pt-2 border-t border-border gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  {scn.watch_enabled ? (
                    <Bell className="h-4 w-4 text-accent" strokeWidth={1.8} />
                  ) : (
                    <BellOff className="h-4 w-4 text-muted-foreground" strokeWidth={1.8} />
                  )}
                  <span className="text-xs text-muted-foreground">
                    Alert when target met
                  </span>
                  <Switch
                    data-testid={`watch-toggle-${scn.scenario_id}`}
                    checked={scn.watch_enabled}
                    onCheckedChange={() => handleToggleWatch(scn)}
                  />
                </div>
                <Button
                  data-testid={`execute-scenario-${scn.scenario_id}`}
                  variant="default"
                  size="sm"
                  onClick={() => handleExecute(scn)}
                  disabled={executingId === scn.scenario_id}
                  className="h-7 text-xs"
                >
                  {executingId === scn.scenario_id ? (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-1 h-3 w-3" strokeWidth={1.8} />
                  )}
                  Mark Executed
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default SavedScenariosList;
